import { getBaseUrl, getWebBaseUrl } from '../config/appSettings';
import { getAuthHeaders } from '../config/apiConfig';

/**
 * Correlation + frontend error reporting (see FINANCIAL_LOGGING_CONTRACT.md).
 *
 * - Every request to our own API gets a fresh X-Request-Id GUID (the API echoes it in its log lines).
 * - A failed call (network error, HTTP error, or a business error in the response body) is remembered as "the last failure".
 * - The exact text the user sees in an error toast is attached to that failure (noteDisplayedError, called by ToastContext).
 * - Shortly after, the failure is sent once to POST /ClientLogs/Error (fire-and-forget; never affects the UI).
 * Nothing here changes request/response behaviour; every step is wrapped in try/catch.
 */

export const REQUEST_ID_HEADER = 'X-Request-Id';
const REPORT_PATH = '/ClientLogs/Error';
const REPORT_DELAY_MS = 1500; // lets the toast text arrive before reporting
const TOAST_ATTACH_WINDOW_MS = 5000;
const DEDUPE_WINDOW_MS = 10000;
const MAX_BODY_CHARS = 4096;

let lastFailure = null;
const reportedCorr = new Set();
const recentSignatures = new Map();

const trimSlash = (url) => String(url || '').replace(/\/+$/, '').toLowerCase();

const isOwnApiUrl = (url) => {
  const target = String(url || '').toLowerCase();
  if (!target) return false;
  return [getBaseUrl(), getWebBaseUrl()]
    .map(trimSlash)
    .some((base) => base && target.startsWith(base));
};

const isReportEndpoint = (url) => String(url || '').toLowerCase().split('?')[0].endsWith(REPORT_PATH.toLowerCase());

export const newCorrelationId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  const hex = (n) => Math.floor(Math.random() * n).toString(16);
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) =>
    c === 'x' ? hex(16) : ((Math.floor(Math.random() * 16) & 0x3) | 0x8).toString(16)
  );
};

const SECRET_KEYS = 'password|pwd|pass|token|accessToken|refreshToken|authorization|secret|apiKey|api_key|clientSecret|cardNumber|cvv|iban|accountNumber|bankAccount|walletAddress';
const JSON_SECRET = new RegExp(`("(?:${SECRET_KEYS})"\\s*:\\s*)("(?:\\\\.|[^"\\\\])*"|[^,}\\]\\s]+)`, 'gi');
const KV_SECRET = new RegExp(`((?:^|[^\\w"])(?:${SECRET_KEYS})\\s*=\\s*)([^&\\s,;"]+)`, 'gi');

/** Masks secrets, flattens to one line and truncates. Never throws. */
export const sanitizeForReport = (text, max = MAX_BODY_CHARS) => {
  try {
    let s = String(text ?? '');
    s = s.replace(JSON_SECRET, '$1"***"').replace(KV_SECRET, '$1***').replace(/Bearer\s+[\w\-._~+/=]+/gi, 'Bearer ***');
    s = s.replace(/[\r\n]+/g, ' ');
    return s.length > max ? `${s.slice(0, max)}...[truncated]` : s;
  } catch {
    return '';
  }
};

const pathOf = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url || '').split('?')[0];
  }
};

const rememberFailure = (failure) => {
  lastFailure = failure;
  setTimeout(() => reportFailure(failure), REPORT_DELAY_MS);
};

function reportFailure(failure) {
  try {
    if (!failure || failure.reported || reportedCorr.has(failure.corr)) return;
    const signature = `${failure.method} ${failure.endpoint} ${failure.httpStatus ?? ''} ${failure.displayedMessage || failure.responseBody || ''}`.slice(0, 300);
    const now = Date.now();
    const seen = recentSignatures.get(signature);
    if (seen && now - seen < DEDUPE_WINDOW_MS) {
      failure.reported = true;
      return;
    }
    recentSignatures.set(signature, now);
    if (recentSignatures.size > 100) {
      recentSignatures.forEach((t, k) => {
        if (now - t > DEDUPE_WINDOW_MS) recentSignatures.delete(k);
      });
    }
    failure.reported = true;
    reportedCorr.add(failure.corr);
    if (reportedCorr.size > 200) reportedCorr.clear();

    const payload = {
      corr: failure.corr,
      action: `${failure.method} ${failure.endpoint}`,
      page: failure.page,
      endpoint: failure.endpoint,
      method: failure.method,
      httpStatus: failure.httpStatus,
      responseBody: failure.responseBody,
      displayedMessage: failure.displayedMessage,
      exception: failure.exception,
      clientTime: failure.clientTime,
    };
    const body = JSON.stringify(payload);
    if (body.length > 7800) return;

    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    // Goes through window.fetch like everything else; the wrapper below skips the report endpoint itself (no loops).
    window
      .fetch(`${getBaseUrl()}${REPORT_PATH}`, { method: 'POST', headers, body, keepalive: true })
      .catch(() => {});
  } catch {
    // reporting must never affect the app
  }
}

/** Called by the toast layer with the exact text shown to the user for an error. */
export const noteDisplayedError = (message) => {
  try {
    const f = lastFailure;
    if (!f || f.displayedMessage || f.reported) return;
    if (Date.now() - f.at > TOAST_ATTACH_WINDOW_MS) return;
    f.displayedMessage = sanitizeForReport(message, 1000);
  } catch {
    // ignore
  }
};

// POST /Rewards/ClaimReward is inverted on the API: responseCode 1 = claimed, 0 = rejected.
const INVERTED_CODE_ENDPOINTS = ['/rewards/claimreward'];

const businessError = (text, endpoint) => {
  try {
    const data = JSON.parse(text);
    const code = data?.response?.responseCode ?? data?.responseCode;
    if (typeof code !== 'number') return false;
    if (INVERTED_CODE_ENDPOINTS.includes(String(endpoint || '').toLowerCase())) return code === 0;
    return code !== 0;
  } catch {
    return false;
  }
};

export function installClientErrorReporter() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.__clientErrorReporterInstalled) {
    return;
  }
  window.__clientErrorReporterInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    let url;
    let method = 'GET';
    let corr;
    let nextInit = init;
    let tracked = false;

    try {
      url = typeof input === 'string' ? input : input?.url;
      if (isOwnApiUrl(url) && !isReportEndpoint(url)) {
        tracked = true;
        method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        corr = headers.get(REQUEST_ID_HEADER) || newCorrelationId();
        headers.set(REQUEST_ID_HEADER, corr);
        nextInit = { ...(init || {}), headers };
      }
    } catch {
      tracked = false;
      nextInit = init;
    }

    if (!tracked) {
      return originalFetch(input, init);
    }

    const base = () => ({
      corr,
      method,
      endpoint: pathOf(url),
      page: (window.location && window.location.pathname) || '',
      clientTime: new Date().toISOString(),
      at: Date.now(),
      reported: false,
    });

    let response;
    try {
      response = await originalFetch(input, nextInit);
    } catch (error) {
      try {
        rememberFailure({
          ...base(),
          exception: sanitizeForReport(`${error?.name || 'Error'}: ${error?.message || error}`, 2000),
        });
      } catch {
        // ignore
      }
      throw error;
    }

    try {
      const status = response.status;
      if (status !== 401) {
        if (!response.ok) {
          const text = await response.clone().text().catch(() => '');
          rememberFailure({ ...base(), httpStatus: status, responseBody: sanitizeForReport(text) });
        } else if (method !== 'GET') {
          // The API reports business errors as HTTP 200 + responseCode != 0; only mutations are inspected (cheap).
          const type = response.headers.get('content-type') || '';
          if (type.includes('json')) {
            const text = await response.clone().text().catch(() => '');
            if (text && text.length < 200000 && businessError(text, pathOf(url))) {
              rememberFailure({ ...base(), httpStatus: status, responseBody: sanitizeForReport(text) });
            }
          }
        }
      }
    } catch {
      // never affect the caller
    }

    return response;
  };
}
